import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import ResumeViewer from './ResumeViewer';
import {
  User,
  FileText,
  BarChart3,
  CreditCard,
  Settings,
  LogOut,
  Crown,
  Zap,
  TrendingUp,
  Calendar,
  Award,
  ChevronRight,
  Plus,
  Eye,
  Download,
  Star,
  Edit,
  MoreVertical
} from 'lucide-react';

const UserDashboard = () => {
  const { user, logout, getUserProfile, updateUserStats } = useAuth();
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [resumeHistory, setResumeHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedResumeId, setSelectedResumeId] = useState(null);
  const [showResumeViewer, setShowResumeViewer] = useState(false);

  useEffect(() => {
    loadUserData();
  }, [user]);

  const loadUserData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Load user profile
      const profile = await getUserProfile(user.uid);
      setUserProfile(profile);

      // Load subscription data
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/subscription/${user.uid}`);
      const subData = await response.json();
      setSubscription(subData);

      // Load analytics
      const analyticsResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/${user.uid}`);
      const analyticsData = await analyticsResponse.json();
      setAnalytics(analyticsData);

      // Load resume history
      const historyResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/resume/history/${user.uid}`);
      const historyData = await historyResponse.json();
      const resumes = historyData.optimizations || historyData.history || [];
      setResumeHistory(resumes);
      
      console.log('📊 Dashboard: Loaded resume history:', {
        total: resumes.length,
        data: resumes
      });

    } catch (error) {
      console.error('Failed to load user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleViewResume = (resumeId) => {
    console.log('🔍 UserDashboard: handleViewResume called with:', resumeId);
    setSelectedResumeId(resumeId);
    setShowResumeViewer(true);
  };

  const handleCloseViewer = () => {
    setShowResumeViewer(false);
    setSelectedResumeId(null);
  };

  const handleDeleteResume = (resumeId) => {
    // Reload user data after deletion
    loadUserData();
  };

  const handleDownloadResume = async (resumeId, format = 'txt') => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/resume/${resumeId}/download?format=${format}`
      );

      if (!response.ok) {
        throw new Error('Failed to download resume');
      }

      // Create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `shortlistly_resume_${resumeId}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

    } catch (error) {
      console.error('Download failed:', error);
      alert('Download failed. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const isPremium = subscription?.plan !== 'free';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-slate-900">Shortlistly</h1>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <img
                  src={user?.photoURL || 'https://images.pexels.com/photos/5921483/pexels-photo-5921483.jpeg'}
                  alt="Profile"
                  className="w-8 h-8 rounded-full"
                />
                <span className="text-slate-700 font-medium">{user?.displayName}</span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 text-slate-600 hover:text-slate-900 transition-colors"
              >
                <LogOut className="w-4 h-4 mr-1" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-2">
            Welcome back, {user?.displayName?.split(' ')[0]}! 👋
          </h2>
          <p className="text-slate-600">
            Here's your career optimization overview
          </p>
        </div>

        {/* Subscription Status */}
        <div className={`mb-8 p-6 rounded-2xl ${isPremium ? 'bg-gradient-to-r from-purple-500 to-blue-600 text-white' : 'bg-yellow-50 border border-yellow-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {isPremium ? (
                <Crown className="w-8 h-8 mr-3" />
              ) : (
                <Zap className="w-8 h-8 text-yellow-600 mr-3" />
              )}
              <div>
                <h3 className={`text-xl font-bold ${isPremium ? 'text-white' : 'text-yellow-900'}`}>
                  {isPremium ? `${subscription?.plan} Plan` : 'Free Plan'}
                </h3>
                <p className={`${isPremium ? 'text-purple-100' : 'text-yellow-700'}`}>
                  {isPremium 
                    ? 'Enjoying unlimited AI-powered optimizations' 
                    : 'Upgrade to unlock unlimited features'}
                </p>
              </div>
            </div>
            {!isPremium && (
              <button
                onClick={() => navigate('/resume-optimizer')}
                className="px-6 py-3 bg-yellow-600 text-white rounded-xl hover:bg-yellow-700 transition-colors font-semibold"
              >
                Upgrade Now
              </button>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-6 shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Resumes Optimized</p>
                <p className="text-3xl font-bold text-slate-900">{resumeHistory.length}</p>
                <p className="text-sm text-green-600">All time</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl p-6 shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Average ATS Score</p>
                <p className="text-3xl font-bold text-slate-900">{resumeHistory.length > 0 ? Math.round(resumeHistory.reduce((acc, r) => acc + (r.ats_score || 0), 0) / resumeHistory.length) : 0}%</p>
                <p className="text-sm text-green-600">+5% this month</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Usage This Month</p>
                <p className="text-3xl font-bold text-slate-900">{resumeHistory.filter(r => {
                  const resumeDate = new Date(r.created_at);
                  const now = new Date();
                  return resumeDate.getMonth() === now.getMonth() && resumeDate.getFullYear() === now.getFullYear();
                }).length}</p>
                <p className="text-sm text-blue-600">Optimizations</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-6 shadow-lg"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Leaderboard Rank</p>
                <p className="text-3xl font-bold text-slate-900">#{userProfile?.stats?.rank || 'N/A'}</p>
                <p className="text-sm text-orange-600">Top performers</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Award className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Quick Actions */}
          <div className="lg:col-span-2">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Quick Actions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <button
                onClick={() => navigate('/career-hub?tab=resume')}
                className="p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-left group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <Plus className="w-6 h-6 text-blue-600" />
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-600" />
                </div>
                <h4 className="font-semibold text-slate-900 mb-2">Optimize New Resume</h4>
                <p className="text-slate-600 text-sm">Create and optimize a new resume with AI</p>
              </button>

              <button
                onClick={() => navigate('/career-hub')}
                className="p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 text-left group"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                    <BarChart3 className="w-6 h-6 text-purple-600" />
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-600" />
                </div>
                <h4 className="font-semibold text-slate-900 mb-2">Career Hub</h4>
                <p className="text-slate-600 text-sm">Access all career optimization tools</p>
              </button>
            </div>

            {/* Recent Resume History */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-slate-900">Recent Optimizations</h3>
                {resumeHistory.length > 3 && (
                  <button
                    onClick={() => navigate('/career-hub')}
                    className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                  >
                    View All
                  </button>
                )}
              </div>
              
              {resumeHistory.length > 0 ? (
                <div className="space-y-4">
                  {resumeHistory.slice(0, 3).map((resume, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">Resume Optimization</p>
                          <p className="text-sm text-slate-500">
                            ATS Score: {resume.ats_score}% • {new Date(resume.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button 
                          onClick={() => {
                            if (resume?.id) {
                              handleViewResume(resume.id);
                            } else {
                              console.error('Resume ID is missing:', resume);
                              alert('Unable to view resume: ID is missing');
                            }
                          }}
                          className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                          title="View Resume"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => {
                            if (resume?.id) {
                              handleViewResume(resume.id);
                            } else {
                              console.error('Resume ID is missing:', resume);
                              alert('Unable to edit resume: ID is missing');
                            }
                          }}
                          className="p-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-colors"
                          title="Edit Resume"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => {
                            if (resume?.id) {
                              handleDownloadResume(resume.id, 'pdf');
                            } else {
                              console.error('Resume ID is missing:', resume);
                              alert('Unable to download resume: ID is missing');
                            }
                          }}
                          className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors"
                          title="Download Resume (PDF)"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <div className="relative group">
                          <button 
                            className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
                            title="More options"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          <div className="absolute right-0 top-8 bg-white border border-slate-200 rounded-lg shadow-lg py-2 opacity-0 group-hover:opacity-100 transition-opacity z-10 whitespace-nowrap">
                            <button 
                              onClick={() => handleDownloadResume(resume.id, 'txt')}
                              className="block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                            >
                              📄 Download as TXT
                            </button>
                            <button 
                              onClick={() => handleDownloadResume(resume.id, 'pdf')}
                              className="block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                            >
                              📋 Download as PDF
                            </button>
                            <button 
                              onClick={() => handleDownloadResume(resume.id, 'docx')}
                              className="block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                            >
                              📝 Download as DOCX
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h4 className="text-slate-600 font-medium mb-2">No optimizations yet</h4>
                  <p className="text-slate-500 text-sm mb-4">Start by optimizing your first resume</p>
                  <button
                    onClick={() => navigate('/career-hub?tab=resume')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Get Started
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Profile Card */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <div className="text-center mb-4">
                <img
                  src={user?.photoURL || 'https://images.pexels.com/photos/5921483/pexels-photo-5921483.jpeg'}
                  alt="Profile"
                  className="w-20 h-20 rounded-full mx-auto mb-4"
                />
                <h3 className="text-lg font-bold text-slate-900">{user?.displayName}</h3>
                <p className="text-slate-600 text-sm">{user?.email}</p>
              </div>
              
              {/* Member Since */}
              <div className="flex items-center justify-center text-sm text-slate-500 mb-4">
                <Calendar className="w-4 h-4 mr-1" />
                Member since {userProfile ? new Date(userProfile.createdAt).toLocaleDateString() : 'Recently'}
              </div>

              {/* Achievements */}
              <div className="border-t border-slate-200 pt-4">
                <h4 className="font-semibold text-slate-900 mb-3">Achievements</h4>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-xs font-medium">
                    Early Adopter
                  </span>
                  {userProfile?.stats?.totalResumes > 0 && (
                    <span className="px-3 py-1 bg-green-100 text-green-600 rounded-full text-xs font-medium">
                      First Optimization
                    </span>
                  )}
                  {isPremium && (
                    <span className="px-3 py-1 bg-purple-100 text-purple-600 rounded-full text-xs font-medium flex items-center">
                      <Crown className="w-3 h-3 mr-1" />
                      Premium
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Recommendations */}
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <h3 className="text-lg font-bold text-slate-900 mb-4">Recommendations</h3>
              <div className="space-y-3">
                {analytics?.recommendations?.slice(0, 3).map((rec, index) => (
                  <div key={index} className="flex items-start">
                    <Star className="w-4 h-4 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-slate-700">{rec}</p>
                  </div>
                )) || (
                  <p className="text-sm text-slate-500">Complete your first optimization to get personalized recommendations!</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Resume Viewer Modal */}
      {showResumeViewer && selectedResumeId && (
        <ResumeViewer
          resumeId={selectedResumeId}
          user={user}
          onClose={handleCloseViewer}
          onDelete={handleDeleteResume}
        />
      )}
    </div>
  );
};

export default UserDashboard;